import { humanReadableField } from '~/filters/humanReadableField';
import { newTrigramTypePipe } from '~/filters/newTrigramTypePipe';
import { displayData } from '~/helpers';
import { FieldClass, FieldObject } from '~/interfaces/field.interface';
import { DeviceGroupModule } from '~/store/modules/devicegroup';
import { RoomsModule } from '~/store/modules/rooms';
import { castToMajorMinorFix } from '~common-helpers';


export function id(value: string): FieldObject {
    return {
        key: 'macAddress',
        value,
        type: 'string',
        tooltip: 'WIFI MAC address',
    };
}

export function name(value?: string): FieldObject {
    return {
        key: 'name',
        value,
        type: 'string',
        tooltip: 'Name given by the user',
    };
}

export function button(value: string, totalNumberOfButtons: number): FieldObject {
    if (totalNumberOfButtons === 0) {
        return { key: 'button', value: 'left (1/1)', type: 'string' };
    } else {
        let button = '';
        const buttonId = value?.split('#')[1];
        switch (buttonId) {
            case '1':
                button = 'right';
                break;
            case '2':
                button = totalNumberOfButtons === 2 ? 'left' : 'middle';
                break;
            case '3':
                button = 'middle';
                break;
        }
        return { key: 'button', value: `${button} (${buttonId}/${totalNumberOfButtons})`, type: 'string' };
    }
}

export function nameLeft(value?: string): FieldObject {
    return { key: 'nameLeft', value, type: 'string' };
}

export function nameRight(value?: string): FieldObject {
    return { key: 'nameRight', value, type: 'string' };
}

export function type(deviceType: string, subtype ?: string): FieldObject {
    const readableValue = humanReadableField(deviceType);
    const newTrigram = newTrigramTypePipe(deviceType);
    // type show will be (newTrigram/oldTrigram) if newTrigram is different than oldTrigram
    // otherwise if it's the same or it doesn't exist, type will show (oldTrigram)

    let toDisplay;

    if (subtype) {
        toDisplay = `${subtype}/${deviceType}`;
    } else if (!subtype && newTrigram && newTrigram !== deviceType) {
        toDisplay = `${newTrigram}/${deviceType}`;
    } else {
        toDisplay = deviceType;
    }


    return {
        key: 'type',
        value: `${readableValue} (${toDisplay})`,
        type: 'string',
        tooltip: 'Type',
    };
}

export function room(value: string): FieldObject {
    const currentRoom = RoomsModule.roomFromId(value);
    return {
        key: 'room',
        value: `${currentRoom?.name} (${value})`,
        type: 'string',
        tooltip: 'Room name given by the user',
    };
}

export function roomLeft(value?: string): FieldObject {
    const currentRoom = RoomsModule.roomFromId(value || '');
    return { key: 'roomLeft', value: `${currentRoom?.name} (${value})`, type: 'string' };
}

export function roomRight(value?: string): FieldObject {
    const currentRoom = RoomsModule.roomFromId(value || '');
    return { key: 'roomRight', value: `${currentRoom?.name} (${value})`, type: 'string' };
}

export function firmware(firmware?: string | number, type?: string, deviceGroupId?: string, hardwareVersion?: number, firmwarePrivate?: number): FieldObject {
    const formattedFirmware = castToMajorMinorFix(Number(firmware), type) || firmware;
    let lastStableFirmware = 0;
    if (deviceGroupId && hardwareVersion) {
        lastStableFirmware = DeviceGroupModule.deviceGroupById(deviceGroupId)?.deployments?.netatmo_devices?.[hardwareVersion]?.release?.firmware_version as number;
    }
    // display firmware in red if the firmware is inferior to the last stable version found for the device group and hardware version of the device
    return {
        key: 'firmware',
        value: formattedFirmware === -1 ? 'N/A' : formattedFirmware,
        class: { 'error--text': (Number(firmwarePrivate || firmware) || -1) < lastStableFirmware },
        tooltip: 'Firmware version',
    };
}

export function firmwareName(firmwareName: string | undefined): FieldObject {
    return {
        key: 'firmwareName',
        value: firmwareName ?? 'N/A',
        class: { 'error--text': !firmwareName },
        tooltip: 'Firmware name',
    };
}

export function firmwareCameras(value?: string | number): FieldObject {
    return {
        key: 'firmware',
        value,
        tooltip: 'Firmware version',
    };
}

export function subtype(value?: string): FieldObject {
    return {
        key: 'subtype',
        value,
        type: 'string',
    };
}

export function zigBeeAddress(value?: string): FieldObject {
    return {
        key: 'macAddressZigbee',
        value,
        tooltip: 'Zigbee MAC address',
    };
}

export function lastUpdate(value?: string): FieldObject {
    return {
        key: 'lastUpdate',
        value,
        type: 'date',
        tooltip: 'Last firmware update date',
    };
}

export function lastReset(value?: string): FieldObject {
    return {
        key: 'lastReset',
        value,
        type: 'date',
    };
}

export function setupDate(value?: number, classApplied?: string): FieldObject {
    return {
        key: 'setupDate',
        value,
        type: 'date',
        class: classApplied,
        tooltip: 'First installation date',
    };
}

export function homekitStatus(value?: string): FieldObject {
    return { key: 'homekitStatus', value, type: 'string' };
}

export function status(value?: string, classApplied?: string): FieldObject {
    return { key: 'status', value, class: classApplied };
}

export function lastStatusStored(value?: number, classApplied?: FieldClass | string): FieldObject {
    return {
        key: 'lastStatusStored',
        value,
        type: 'date',
        class: classApplied,
        tooltip: 'Date of last update with Cloud',
    };
}

export function hwVersion(value?: number | string): FieldObject {
    return {
        key: 'hwVersion',
        value,
        type: 'subField',
        subfield: 'hw_version',
        class: { 'error--text': (Number(value) || -1) < 0 },
    };
}

export function customer(value?: string): FieldObject {
    return { key: 'customer', value, type: 'string' };
}

export function customer2(value?: string): FieldObject {
    return { key: 'customer2', value, type: 'string' };
}

export function command(value?: string, link?: string): FieldObject {
    return { key: 'command', value, type: 'internalLink', link };
}

export function command2(value?: string, link?: string): FieldObject {
    return { key: 'command2', value, type: 'internalLink', link };
}

export function uptime(value?: number): FieldObject {
    return {
        key: 'uptime',
        value,
        type: 'duration',
        tooltip: 'operational time',
    };
}

export function wifiChannel(value?: number): FieldObject {
    return {
        key: 'wifiChannel',
        value,
        type: 'number',
        tooltip: 'WIFI channel used by the device',
    };
}

export function fwPrivate(value?: number): FieldObject {
    return { key: 'fwPrivate', value };
}

export function lastSetup(value?: number): FieldObject {
    return {
        key: 'lastSetup',
        value,
        type: 'date',
        tooltip: 'Last installation date',
    };
}

export function controlledDevices(value?: string []): FieldObject {
    return { key: 'controlledDevices', value, type: 'array' };
}

export function controlledDevicesLeft(value?: string[]): FieldObject {
    return { key: 'controlledDevicesLeft', value, type: 'array' };
}

export function controlledDevicesMiddle(value?: string[]): FieldObject {
    return { key: 'controlledDevicesMiddle', value, type: 'array' };
}

export function controlledDevicesRight(value?: string[]): FieldObject {
    return { key: 'controlledDevicesRight', value, type: 'array' };
}

export function battery(icon: string, value?: number): FieldObject {
    return {
        key: 'battery',
        value,
        type: 'icon',
        icon,
        tooltip: 'Battery level (value in mV)',
    };
}

export function lastSeen(value?: number | string, classApplied?: string): FieldObject {
    return {
        key: 'lastSeen', value,
        type: 'date',
        class: classApplied,
        tooltip: 'Date of last communication with Cloud',
    };
}

export function reachable(value?: boolean): FieldObject {
    return {
        key: 'reachable',
        value,
        type: 'boolean',
        tooltip: 'True if the device is reachable by the cloud',
    };
}

export function configured(value?: boolean): FieldObject {
    return {
        key: 'configured',
        value,
        type: 'boolean',
        tooltip: 'True if the device is configured',
    };
}

export function relayConnected(value?: string): FieldObject {
    return { key: 'relayConnectedTo', value, type: 'subField', subfield: 'plug_connected_boiler' };
}

export function mfgId(value?: number): FieldObject {
    return { key: 'mfgId', value };
}

export function applianceType(value?: string): FieldObject {
    return {
        key: 'applianceType',
        value,
        type: 'string',
        tooltip: 'Could be: Socket, Light, Multimedia, etc.',
    };
}

export function mode(value?: string): FieldObject {
    return {
        key: 'mode',
        value,
        type: 'string',
        tooltip: 'Total or Appliance',
    };
}

export function energyType(value?: string): FieldObject {
    return {
        key: 'energyType',
        value,
        type: 'string',
        tooltip: 'Consumption or Production',
    };
}

export function fullFeatureSupported(value?: boolean): FieldObject {
    return { key: 'fullFeatureSupported', value, type: 'boolean' };
}

export function packaging(value?: string): FieldObject {
    return { key: 'packaging', value };
}

export function packagingId(value?: string): FieldObject {
    return { key: 'packagingId', value };
}

export function line(value?: string): FieldObject {
    return { key: 'line', value, type: 'string' };
}

export function subline(value?: string): FieldObject {
    return { key: 'subline', value, type: 'string' };
}

export function lineName(value?: string): FieldObject {
    return { key: 'lineName', value, type: 'string' };
}

export function ergonomicId(value?: string): FieldObject {
    return { key: 'ergonomicId', value, type: 'string' };
}

export function sdStatus(value?: number, classApplied?: string): FieldObject {
    return { key: 'sdStatus', value, type: 'subField', subfield: 'sd_status', class: classApplied };
}

export function alimStatus(value?: number, classApplied?: string): FieldObject {
    return { key: 'alimStatus', value, type: 'subField', subfield: 'alim_status', class: classApplied };
}

export function monitoring(value?: string, classApplied?: string): FieldObject {
    return { key: 'monitoring', value, type: 'string', class: classApplied };
}

export function installationVoltage(value?: number): FieldObject {
    return { key: 'installationVoltage', value };
}

export function installationIlim(value?: number): FieldObject {
    return { key: 'installationIlim', value };
}

export function stateCharge(value?: number): FieldObject {
    return { key: 'stateOfCharge', value, type: 'percent' };
}

export function stateOfHealth(value?: number): FieldObject {
    return { key: 'stateOfHealth', value, type: 'percent' };
}

export function nbCharge(value?: number): FieldObject {
    return { key: 'nbCharge', value };
}

export function batteryTemperatureLevel(value?: number): FieldObject {
    return { key: 'batteryTemperatureLevel', value };
}

export function tamperDetection(value?: boolean, classApplied?: string): FieldObject {
    return { key: 'tamperDetection', value, type: 'boolean', class: classApplied };
}

export function alimentation(value?: string): FieldObject {
    return { key: 'alimentation', value, type: 'string' };
}

export function lastActivity(value?: number): FieldObject {
    return { key: 'lastActivity', value, type: 'date' };
}

export function autoTrigger(value?: string, classApplied?: string): FieldObject {
    return { key: 'autoTrigger', value, type: 'string', class: classApplied };
}

export function notifyRule(value?: string): FieldObject {
    return { key: 'notifyRule', value, type: 'alert_record' };
}

export function recordRule(value?: string): FieldObject {
    return { key: 'recordRule', value, type: 'alert_record' };
}

export function rfRating(value?: number): FieldObject {
    return { key: 'rfRating', value, type: 'percent' };
}

export function sirenSoundRule(value?: string): FieldObject {
    return { key: 'sirenSoundRule', value, type: 'alert_record' };
}

export function sirenEntryDelay(value?: string): FieldObject {
    return { key: 'sirenEntryDelay', value, type: 'alert_record' };
}

export function lastData(value?: number, classApplied?: string): FieldObject {
    return { key: 'lastData', value, type: 'date', class: classApplied };
}

export function radioId(value?: string): FieldObject {
    return { key: 'radioId', value };
}

export function wifiChip(value?: string): FieldObject {
    return { key: 'wifiChip', value, type: 'string' };
}

export function shield(value?: boolean): FieldObject {
    return { key: 'shield', value, type: 'boolean' };
}

export function motorSpeed(value?: number): FieldObject {
    return { key: 'motorSpeed', value };
}

export function voltageDrop(value?: number): FieldObject {
    return { key: 'voltageDrop', value };
}

export function batteryDead(value?: boolean): FieldObject {
    return { key: 'batteryDead', value, type: 'boolean' };
}

export function pidAlgoPeriod(value?: number): FieldObject {
    return { key: 'pidAlgoPeriod', value };
}

export function lastResetType(value?: string): FieldObject {
    return { key: 'lastResetType', value };
}

export function country(value?: string): FieldObject {
    return { key: 'Country', value, type: 'string' };
}

export function zipCode(value?: string): FieldObject {
    return { key: 'Zip Code', value, type: 'string' };
}

export function address(value?: string): FieldObject {
    return { key: 'Address', value, type: 'string' };
}

export function groupId(value?: number): FieldObject {
    return { key: 'groupId', value };
}

export function veluxType(value?: string): FieldObject {
    return { key: 'veluxType', value };
}

export function routerId(value?: string): FieldObject {
    return { key: 'routerId', value };
}

export function shareWeathermap(value?: boolean): FieldObject {
    return { key: 'shareWeatherMap', value, type: 'boolean' };
}

export function weatherMapIsFiltered(value?: boolean): FieldObject {
    return { key: 'weatherMapIsFiltered', value, type: 'boolean' };
}

export function weatherMapFilteringReason(value?: string): FieldObject {
    return { key: 'weatherMapFilteringReason', value };
}

export function stationName(value?: string): FieldObject {
    return { key: 'stationName', value };
}

export function boilerControl(value?: string): FieldObject {
    return { key: 'boilerControl', value };
}

export function dhwControl(value?: string): FieldObject {
    return { key: 'dhwControl', value };
}

export function bubendorffMotorType(value?: string): FieldObject {
    return { key: 'bubendorffMotorType', value, class: 'bold' };
}

export function thermalControl(value?: boolean): FieldObject {
    return { key: 'thermalControl', value, type: 'boolean' };
}

export function calibrationMinMax(value?: string): FieldObject {
    return { key: 'calibrationMinMax', value };
}

export function lastCalibration(value?: number): FieldObject {
    return { key: 'lastCalibration', value, type: 'date' };
}

export function eventualDoubleMac(value?: boolean): FieldObject {
    return { key: 'eventualDoubleMac', value, type: 'boolean' };
}

export function ticStatus(value?: boolean): FieldObject {
    return { key: 'ticStatus', value: value ? 'connected' : 'not connected', type: 'string' };
}

export function ticType(value: number): FieldObject {
    return { key: 'ticType', value: value >= 0 ? 'standard' : 'historic', type: 'string' };
}

export function refurbished(value?: number): FieldObject {
    return { key: 'refurbished', value, type: 'date' };
}

export function migrated(value?: boolean): FieldObject {
    return { key: 'migrated', value, type: 'boolean', class: value ? 'success--text' : 'error--text' };
}

export function keyAttachments(attachments: Array<{ id: string, name: string, setup_date: number }>) {
    const value = attachments.map(attachment => `${attachment.id} - ${attachment.name} - ${displayData({
        key: '',
        value: attachment.setup_date,
        type: 'date',
    })}`);
    return { key: 'keyAttachments', value, type: 'array' };
}

export function manufacturer(value?: string): FieldObject {
    return { key: 'manufacturer', value };
}

export function image(value?: string): FieldObject {
    return { key: 'image', value };
}

export function feedbackGiven(value?: boolean): FieldObject {
    return { key: 'feedbackGiven', value, type: 'boolean' };
}

export function powerSource(value?: string): FieldObject {
    return { key: 'powerSource', value };
}
